import React, { useEffect, useRef, useState } from 'react';
import {
  Routes,
  Route,
  useLocation,
} from 'react-router-dom';
import moment from 'moment';
import { store } from './AppProvider.js';
import { useSelector, useDispatch } from 'react-redux';
import { getAcl, getMenuAcl } from './api/acl';

import { StateType } from './types/StateType';

import ContentPage from './pages/ContentPage.jsx';
import StatsPage from './pages/StatsPage';
import StartPage from './pages/StartPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import Login from './pages/Login.jsx';
import SetPassword from './pages/SetPassword.jsx';
import Monitoring from './pages/Monitoring.jsx';
import { Toaster } from "./components/ui/toaster"

import FilterbarDesktop from './components/Filterbar/FilterbarDesktop.jsx';
import FilterbarMobile from './components/Filterbar/FilterbarMobile.jsx';
import About from './components/About/About.jsx';
import Features from './components/Features/Features';
import VeelgesteldeVragen from './components/VeelgesteldeVragen/VeelgesteldeVragen';
import Tour from './components/Tour/Tour.jsx';
import Overlay from './components/Overlay/Overlay.jsx';
import Misc from './components/Misc/Misc.jsx';
import SidebarLayout from './components/SidebarLayout/SidebarLayout';
import Docs from './components/Docs/Docs';
import Faq from './components/Faq/Faq';
import Profile from './components/Profile/Profile';
import Export from './components/Export/Export';
import ActiveFeeds from './components/ActiveFeeds/ActiveFeeds';
import MailTemplateList from './components/MailTemplateList/MailTemplateList';
import MapPage from './pages/MapPage.jsx';
import Menu from './components/Menu';
import IntroModal from './components/IntroModal/IntroModal';
import FloatingMobileMenu from './components/Menu/FloatingMobileMenu.jsx';
import {SelectLayerMobile} from './components/SelectLayer/SelectLayerMobile.jsx';
import LoadingIndicator from './components/LoadingIndicator/LoadingIndicator.jsx';
import LoginStats from './components/LoginStats/LoginStats';
import UserList from './components/UserList/UserList';
import OrganisationList from './components/OrganisationList/OrganisationList';
import SharedDataOverview from './components/SharedDataOverview/SharedDataOverview';
import YearlyCostsExport from './components/YearlyCostsExport/YearlyCostsExport';
import EmailUsers from './components/EmailUsers/EmailUsers';
import ApiKeys from './components/ApiKeys/ApiKeys';

import { initAccessControlList } from './poll-api/metadataAccessControlList.js';
import { updateZones } from './poll-api/metadataZones.js';
import { updateBeleidszonesZones } from './poll-api/metadataZonesBeleidszones.js';
import { updateZonesgeodata } from './poll-api/metadataZonesgeodata.js';

import {setAclInRedux} from './actions/authentication.js';
import { initUpdateParkingData } from './poll-api/pollParkingData.js';
import {
  initUpdateVerhuringenData
} from './poll-api/pollVerhuringenData.js';

import {
  notifyInfo,
  notifyError,
} from './helpers/notify';
import { pathRequiresBackgroundMap } from './helpers/routes';

import {
  isValidAuthState,
  clearInvalidAuthState,
} from './helpers/authentication.js';

import {
  DISPLAYMODE_PARK,
  DISPLAYMODE_RENTALS,
  DISPLAYMODE_ZONES_PUBLIC,
  DISPLAYMODE_ZONES_ADMIN,
  DISPLAYMODE_OTHER,
  DISPLAYMODE_SERVICE_AREAS,
  DISPLAYMODE_POLICY_HUBS,
  DISPLAYMODE_START,
  DISPLAYMODE_DASHBOARD,
  DISPLAYMODE_PARKEERDATA_VOERTUIGEN,
} from './reducers/layers.js';
import { setSingleDataLayer } from './actions/layers';

import './App.css';

declare global {
  interface Window {
    notify?: (msg: string) => void;
  }
}

interface Acl {
  privileges?: string[];
  is_admin?: boolean;
  [key: string]: unknown;
}

function App() {
  // Our state variables
  const [pathName, setPathName] = useState<string>(document.location.pathname);
  const [uriParams, setUriParams] = useState<string>(document.location.search);
  const [delayTimeout, setDelayTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [isOrganisationAdmin, setIsOrganisationAdmin] = useState<boolean>(false);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [acl, setAcl] = useState<Acl>({});

  const dispatch = useDispatch();

  const token = useSelector((state: StateType) => (state.authentication.user_data && state.authentication.user_data.token) || null);
  
  const mapContainer = useRef<HTMLDivElement | null>(null);

  const DELAY_TIMEOUT_IN_MS = 250;

  // The park/rentals data effects debounce their API poll to coalesce rapid
  // filter changes. The very first poll doesn't need that: fire it immediately
  // so the (large) vehicles request starts as early as possible. Follow-up
  // effect runs during app boot no longer abort it (see poll-api/pollParkingData).
  const didInitParkingPoll = useRef(false);
  const didInitRentalsPoll = useRef(false);

  // Store window location in a local variable
  const location = useLocation();
  useEffect(() => {
    setPathName(location?.pathname ?? '');
    setUriParams(location?.search ?? '');
  }, [location]);

  // Update page title based on route
  useEffect(() => {
    const baseTitle = 'Dashboard Deelmobiliteit | Een project van en voor overheden';
    
    const getPageTitle = (pathname: string): string => {
      // Exact matches first
      if (pathname === '/' || pathname === '/map/park') {
        return `Aanbod - ${baseTitle}`;
      }
      if (pathname === '/map/rentals') {
        return `Verhuringen - ${baseTitle}`;
      }
      if (pathname === '/map/zones') {
        return `Zones - ${baseTitle}`;
      }
      if (pathname === '/map/servicegebieden') {
        return `Servicegebieden - ${baseTitle}`;
      }
      if (pathname === '/map/beleidshubs') {
        return `Zones - ${baseTitle}`;
      }
      if (pathname === '/stats/beleidsinfo') {
        return `Ontwikkeling - ${baseTitle}`;
      }
      if (pathname === '/over') {
        return `Over - ${baseTitle}`;
      }
      if (pathname === '/functionaliteiten') {
        return `Wat kun je ermee? - ${baseTitle}`;
      }
      if (pathname === '/faq' || pathname === '/veelgestelde-vragen') {
        return `Veelgestelde vragen - ${baseTitle}`;
      }
      if (pathname === '/docs') {
        return `Documentatie - ${baseTitle}`;
      }
      if (pathname === '/export') {
        return `Export - ${baseTitle}`;
      }
      if (pathname === '/active_feeds') {
        return `Actieve feeds - ${baseTitle}`;
      }
      if (pathname === '/rondleiding') {
        return `Rondleiding - ${baseTitle}`;
      }
      if (pathname === '/monitoring') {
        return `Monitoring - ${baseTitle}`;
      }
      if (pathname === '/login') {
        return `Inloggen - ${baseTitle}`;
      }
      if (pathname === '/profile') {
        return `Start - ${baseTitle}`;
      }
      if (pathname === '/profile/api') {
        return `API-sleutels - ${baseTitle}`;
      }
      
      // Dynamic routes
      if (pathname.startsWith('/stats/')) {
        const dashboard = pathname.split('/stats/')[1]?.split('/')[0];
        if (dashboard === 'prestaties-aanbieders') {
          return `Prestaties aanbieders - ${baseTitle}`;
        }
        if (dashboard === 'prestaties-aanbieders-details') {
          return `Prestaties aanbieders details - ${baseTitle}`;
        }
        if (dashboard === 'beleidszones') {
          return `Beleidszones - ${baseTitle}`;
        }
        return `Dashboard - ${baseTitle}`;
      }
      
      if (pathname.startsWith('/admin')) {
        if (pathname === '/admin' || pathname === '/admin/users') {
          return `Gebruikers - Beheer - ${baseTitle}`;
        }
        if (pathname === '/admin/users/new') {
          return `Nieuwe gebruiker - Beheer - ${baseTitle}`;
        }
        if (pathname.startsWith('/admin/users/')) {
          return `Gebruiker - Beheer - ${baseTitle}`;
        }
        if (pathname === '/admin/organisations') {
          return `Organisaties - Beheer - ${baseTitle}`;
        }
        if (pathname === '/admin/organisations/new') {
          return `Nieuwe organisatie - Beheer - ${baseTitle}`;
        }
        if (pathname.startsWith('/admin/organisations/')) {
          return `Organisatie - Beheer - ${baseTitle}`;
        }
        if (pathname === '/admin/shared') {
          return `Gedeelde data - Beheer - ${baseTitle}`;
        }
        if (pathname === '/admin/stats') {
          return `Statistieken - Beheer - ${baseTitle}`;
        }
        if (pathname === '/admin/yearly-costs') {
          return `Jaarlijkse kosten - Beheer - ${baseTitle}`;
        }
        if (pathname === '/admin/email') {
          return `E-mail gebruikers - Beheer - ${baseTitle}`;
        }
        if (pathname === '/admin/mail-templates') {
          return `E-mail templates - Beheer - ${baseTitle}`;
        }
        if (pathname === '/admin/mail-templates/new') {
          return `Nieuw e-mail template - Beheer - ${baseTitle}`;
        }
        if (pathname === '/admin/zones') {
          return `Zones - Beheer - ${baseTitle}`;
        }
        return `Beheer - ${baseTitle}`;
      }
      
      if (pathname.startsWith('/reset-password/')) {
        return `Wachtwoord instellen - ${baseTitle}`;
      }
      
      if (pathname.startsWith('/faq/')) {
        return `Veelgestelde vragen - ${baseTitle}`;
      }
      
      if (pathname.startsWith('/docs/')) {
        return `Documentatie - ${baseTitle}`;
      }
      
      // Default fallback
      return baseTitle;
    };
    
    const newTitle = getPageTitle(location.pathname);
    document.title = newTitle;
  }, [location.pathname]);
  
  // Init notify bar logic. Routes through the shared Radix toast so all
  // app notifications use a single, consistent system.
  useEffect(() => {
    window.notify = (msg: string) => {
      notifyInfo(msg);
    };

    return () => {
      window.notify = undefined;
    };
  }, []);

  useEffect(() => {
    if (uriParams !== null) {
      let params = new URLSearchParams(uriParams);
      let view = params.get('view')
      if(view) {
        try {
          const importstate = JSON.parse(decodeURIComponent(view));
          // force update to map extent
          dispatch({type: 'IMPORT_STATE', payload: importstate });
          
          setTimeout(() => dispatch({type: 'LAYER_SET_ZONES_EXTENT', payload: importstate.layers.mapextent}), 2500);
          return;
        } catch(ex) {
          console.warn("unable to decode application state")
          notifyError("Ongeldige link ingegeven");
        }
        // continue
      }
    }
    
    // Decide on which display mode we use, based on URL
    let payload;
    if(pathName==='/') {
      payload=DISPLAYMODE_PARK;
    } else if(pathName.includes("/start")) {
      payload=DISPLAYMODE_START;
    } else if(pathName === "/stats/beleidsinfo") {
      payload=DISPLAYMODE_OTHER;
    } else if(pathName === "/stats/beleidszones") {
      payload=DISPLAYMODE_OTHER;
    } else if(pathName.startsWith("/stats/")) {
      payload=DISPLAYMODE_DASHBOARD;
    } else if(pathName.includes("/map/park")) {
      payload=DISPLAYMODE_PARK;
    } else if(pathName.includes("/map/rentals")) {
      payload=DISPLAYMODE_RENTALS;
    } else if(pathName.includes("/map/zones")) {
      payload=DISPLAYMODE_ZONES_PUBLIC;
    } else if(pathName.includes("/admin/zones")) {
      payload=DISPLAYMODE_ZONES_ADMIN;
    } else if(pathName.includes("/map/servicegebieden")) {
      payload=DISPLAYMODE_SERVICE_AREAS;
    } else if(pathName.includes("/map/beleidshubs")) {
      payload=DISPLAYMODE_POLICY_HUBS;
    } else {
      payload=DISPLAYMODE_OTHER;
    }
    dispatch({type: 'LAYER_SET_DISPLAYMODE', payload});

  }, [pathName, uriParams, dispatch]);

  useEffect(() => {
    const updateACL = async () => {
      try {
        // Validate authentication state before making API calls
        const currentState = store.getState();
        if (!isValidAuthState(currentState)) {
          console.warn("Invalid authentication state detected, clearing user data");
          clearInvalidAuthState(dispatch);
          return;
        }

        const userAcl = await getAcl(token);
        if (!userAcl) return;

        const menuAcl = await getMenuAcl(token);
        const theAcl = {
          ...userAcl,
          ...(menuAcl && !Array.isArray(menuAcl) ? menuAcl : {}),
        };

        dispatch(setAclInRedux(theAcl));
        setAcl(theAcl);
        
        setIsOrganisationAdmin(theAcl?.privileges && theAcl?.privileges.indexOf('ORGANISATION_ADMIN') > -1);
        setIsAdmin(theAcl?.is_admin);
      } catch(err) {
        console.error("ACL update failed:", err);
        
        // If ACL update fails due to authentication issues, clear the user data
        if (err.status === 401 || err.status === 403) {
          console.warn("Authentication failed during ACL update, clearing user data");
          clearInvalidAuthState(dispatch);
        }
      }
    }

    updateACL();
  }, [token, dispatch])

  // const test = useSelector((state: StateType) => {
  //   console.log('*** test', state)
  //   return state.authentication;
  // });

  const isLoggedIn = useSelector((state: StateType) => {
    return state.authentication.user_data ? true : false;
  });

  // Guests start on the cluster view so NL-wide vehicle data stays readable
  // and fast (issue #259). Runs on initial guest load and after logout
  // (LOGOUT resets layers to points). Dependency is only isLoggedIn, so a
  // guest can still switch to points or heatmap afterwards.
  useEffect(() => {
    if (!isLoggedIn) {
      dispatch(setSingleDataLayer(DISPLAYMODE_PARK, DISPLAYMODE_PARKEERDATA_VOERTUIGEN));
    }
  }, [isLoggedIn, dispatch]);
  
  const filterDate = useSelector((state: StateType) => {
    return state.filter ? state.filter.datum : false;
  });

  const isLayersMobileVisible = useSelector((state: StateType) => {
    return state.ui ? state.ui['FloatingMobileMenu.layers'] : false;
  });

  const isFilterBarVisible = useSelector((state: StateType) => {
    return state.ui ? state.ui['FILTERBAR'] : false;
  });

  const filter = useSelector((state: StateType) => {
    return state.filter;
  });
  
  // const layers = useSelector((state) => {
  //   return state.layers;
  // });

  const displayMode = useSelector((state: StateType) => {
    return state.layers ? state.layers.displaymode : DISPLAYMODE_PARK;
  });
  
  const metadata = useSelector((state: StateType) => {
    return state.metadata;
  });

  // Set date to current date/time on load
  useEffect(() => {
    const setFilterDatum = newdt => {
      dispatch({
        type: 'SET_FILTER_DATUM',
        payload: newdt.toISOString()
      })
    }

    // On load, set datetime to NOW
    // - Don't do this if datetime < 10 ago
    // - Don't do this if this is a shared link (we'll then keep the shared datetime)
    const params = new URLSearchParams(uriParams);
    const isFilterDateMoreThan10MinutesAgo = moment(filterDate).diff(moment(), 'minutes') < -10;
    const isSharedLink = params.get('view');// Check if this is a shared link
    if(! isSharedLink && isFilterDateMoreThan10MinutesAgo) {
      setTimeout(x => {
        setFilterDatum(moment().toDate())
      }, 500)
    }
  }, []);

  /*
  To load data using the API we use the scripts in the poll-api folder.
  We need zones to be able to fetch parking & trips data.

  First, on app load, we load user information.
  Then, we load the zones.
  As soon as the zones are loaded, we load the parking data and rentals data.
  We reload the parking data and rentals data if the filters or the URL change.
  */

  // On app start: get user data
  useEffect(() => {
    // Add a small delay to ensure the store is properly initialized
    const timer = setTimeout(() => {
      initAccessControlList(store);
    }, 100);

    return () => clearTimeout(timer);
  }, [isLoggedIn]);
  
  // Only the beleidszones page loads a different set of zones, so reloading on
  // every single path change is wasteful: it briefly resets `zones_loaded`,
  // which retriggers the zone geodata fetch (and used to reset the map viewport).
  const isBeleidszonesPath = Boolean(pathName?.includes('/stats/beleidszones'));
  const beleidszonesFilterZones = isBeleidszonesPath ? filter.zones : null;

  useEffect(() => {
    if(process && process.env.DEBUG) console.log('useEffect zones', filter.gebied)
    if(! metadata.metadata_loaded) return;

    if (isBeleidszonesPath) {
      updateBeleidszonesZones(store);
    } else {
      updateZones(store);
    }
  }, [
    isBeleidszonesPath,
    isLoggedIn,
    metadata.metadata_loaded,
    filter.gebied,
    beleidszonesFilterZones,
  ]);

  /**
   * When navigating away from /stats/beleidszones to a page that doesn't show archived
   * zones (e.g. /stats/beleidsinfo), remove any selected zones from Redux that are
   * not in the current page's zone list. This prevents "1 zone selected" with no
   * visible zone when an archived zone was selected on beleidszones.
   */
  useEffect(() => {
    if (pathName?.includes('/stats/beleidszones')) return;
    if (!metadata.zones_loaded || !metadata.zones?.length) return;
    const zonesStr = filter?.zones;
    if (!zonesStr || typeof zonesStr !== 'string' || !zonesStr.trim()) return;

    const availableZoneIds = new Set(
      metadata.zones.map((z: { zone_id?: number | string }) =>
        z.zone_id != null ? String(z.zone_id) : null
      ).filter(Boolean)
    );
    const selectedIds = zonesStr.split(',').map((id) => id.trim()).filter(Boolean);
    const validIds = selectedIds.filter((id) => availableZoneIds.has(id));
    if (validIds.length < selectedIds.length) {
      store.dispatch({
        type: 'SET_FILTER_ZONES',
        payload: validIds.length > 0 ? validIds.join(',') : ''
      });
    }
  }, [pathName, metadata.zones_loaded, metadata.zones, filter?.zones]);

  useEffect(() => {
    if(process && process.env.DEBUG) console.log('useEffect zones geodata')
    if(! metadata.zones_loaded) return;

    updateZonesgeodata(store);
  }, [
    isLoggedIn,
    metadata.zones_loaded,
    filter.gebied,
    filter.zones
  ])

  // On app start,
  //  if zones are loaded
  //  or if pathName/filter is changed:
  //  reload park events data
  useEffect(() => {
    // displayMode
    if(displayMode !== 'displaymode-park') return;
    if(isLoggedIn && metadata.zones_loaded === false) return;

    if(delayTimeout) clearTimeout(delayTimeout);

    const delay = didInitParkingPoll.current ? DELAY_TIMEOUT_IN_MS : 0;
    didInitParkingPoll.current = true;
    setDelayTimeout(setTimeout(() => {
      initUpdateParkingData(store);
    }, delay))
  }, [
    isLoggedIn,
    metadata.zones_loaded,
    metadata.aclOperators,
    filter,
    DELAY_TIMEOUT_IN_MS,
    displayMode,
    // exportState?.layers.map_style,
  ]);

  // Reload rentals data if i.e. filter changes
  useEffect(() => {
    if(displayMode !== 'displaymode-rentals') return;
    if(isLoggedIn && metadata.zones_loaded === false) return;

    if(delayTimeout) clearTimeout(delayTimeout);

    const delay = didInitRentalsPoll.current ? DELAY_TIMEOUT_IN_MS : 0;
    didInitRentalsPoll.current = true;
    setDelayTimeout(setTimeout(() => {
      initUpdateVerhuringenData(store);
    }, delay))
  }, [
    isLoggedIn,// If we change from guest to logged in we want to update rentals
    metadata.zones_loaded,// We only do an API call if zones are loaded
    metadata.aclOperators,
    filter,
    DELAY_TIMEOUT_IN_MS,
    displayMode,
    // exportState?.layers.map_style,
  ]);

  // Mobile menu: Filters / Layers
  const renderFilterbar = () => {
    return <div className="MobileMenus">
      <div className="hidden sm:block relative h-full z-10">
        <FilterbarDesktop isVisible={isFilterBarVisible} displayMode={displayMode} />
      </div>
      <div className="block sm:hidden relative z-10">
        <FilterbarMobile isVisible={isFilterBarVisible} displayMode={displayMode} />
      </div>
    </div>
  }

  const renderFilterbarElements = () => {
    return <>
      {renderFilterbar()}
      <FloatingMobileMenu />
    </>
  }

  return (
    <div className={`app ${(isFilterBarVisible || isLayersMobileVisible) ? 'overflow-y-hidden' : ''}`}>

      <LoadingIndicator  />

      <div className="gui-layer">

      <Routes>
        { isLoggedIn ?
          <>
            { (isAdmin || isOrganisationAdmin) ?
              <>
                <Route path="/admin" element={
                  <Overlay>
                    <SidebarLayout title="Gebruikers" contentWidth="100%">
                      <UserList acl={acl} />
                    </SidebarLayout>
                  </Overlay>
                } />
                <Route path="/admin/users" element={
                  <Overlay>
                    <SidebarLayout title="Gebruikers" contentWidth="100%">
                      <UserList acl={acl} />
                    </SidebarLayout>
                  </Overlay>
                } />
                <Route path="/admin/users/new" element={
                  <Overlay>
                    <SidebarLayout title="Gebruikers" contentWidth="100%">
                      <UserList
                        acl={acl}
                        showAddUserModule={true} />
                    </SidebarLayout>
                  </Overlay>
                } />
                <Route path="/admin/users/:username" element={
                  <Overlay>
                    <SidebarLayout title="Gebruikers" contentWidth="100%">
                      <UserList acl={acl} />
                    </SidebarLayout>
                  </Overlay>
                } />
                <Route path="/admin/shared" element={
                  <Overlay>
                    <SidebarLayout title="Data delen" contentWidth="100%">
                      <SharedDataOverview />
                    </SidebarLayout>
                  </Overlay>
                } />
                <Route path="/admin/organisations" element={
                  <Overlay>
                    <SidebarLayout title="Organisaties" contentWidth="100%">
                      <OrganisationList />
                    </SidebarLayout>
                  </Overlay>
                } />
                <Route path="/admin/organisations/new" element={
                  <Overlay>
                    <SidebarLayout title="Organisaties" contentWidth="100%">
                      <OrganisationList showAddOrganisationModule={true} />
                    </SidebarLayout>
                  </Overlay>
                } />
                <Route path="/admin/organisations/:organisationId" element={
                  <Overlay>
                    <SidebarLayout title="Organisaties" contentWidth="100%">
                      <OrganisationList />
                    </SidebarLayout>
                  </Overlay>
                } />
                <Route path="/admin/stats" element={
                  <Overlay>
                    <SidebarLayout title="Statistieken" contentWidth="100%">
                      <LoginStats />
                    </SidebarLayout>
                  </Overlay>
                } />
                <Route path="/admin/yearly-costs" element={
                  <Overlay>
                    <SidebarLayout title="Exporteer jaarbijdrage" contentWidth="100%">
                      <YearlyCostsExport />
                    </SidebarLayout>
                  </Overlay>
                } />
                <Route path="/admin/email" element={
                  <Overlay>
                    <SidebarLayout title="E-mail gebruikers" contentWidth="100%">
                      <EmailUsers acl={acl} />
                    </SidebarLayout>
                  </Overlay>
                } />
                <Route path="/admin/mail-templates" element={
                  <Overlay>
                    <SidebarLayout title="Mail-templates" contentWidth="100%">
                      <MailTemplateList />
                    </SidebarLayout>
                  </Overlay>
                } />
                <Route path="/admin/mail-templates/new" element={
                  <Overlay>
                    <SidebarLayout title="Mail-templates" contentWidth="100%">
                      <MailTemplateList showAddMailTemplateModule={true}  />
                    </SidebarLayout>
                  </Overlay>
                } />
              </> : null
            }

            <Route path="/" element={renderFilterbarElements()} />
            <Route path="/map/park" element={renderFilterbarElements()} />
            <Route path="/map/rentals" element={renderFilterbarElements()} />
            <Route path="/map/servicegebieden" element={renderFilterbarElements()} />
            <Route path="/map/beleidshubs" element={renderFilterbarElements()} />

            <Route path="/map/zones" element={renderFilterbarElements()} />
            <Route path="/admin/zones" element={renderFilterbarElements()} />

            <Route path="/stats/beleidsinfo" element={<>
              <ContentPage
                style={{ paddingBottom: 0 }}
                innerStyle={{
                  paddingTop: 0, paddingRight: 0, paddingBottom: 0
                }}
                  >
                <StatsPage />
              </ContentPage>
              {renderFilterbarElements()}
            </>} />
            <Route path="/stats/:dashboard" element={<>
              <ContentPage
                style={{ paddingBottom: 0 }}
                innerStyle={{ paddingTop: 0, paddingRight: 0, paddingBottom: 0
              }}>
                <DashboardPage />
              </ContentPage>
              {renderFilterbarElements()}
            </>} />
            <Route path="/monitoring" element={
              <ContentPage>
                <Monitoring />
              </ContentPage>
            } />
            <Route path="/rondleiding" element={
              <ContentPage forceFullWidth={true}>
                <Tour />
              </ContentPage>
            } />
            <Route path="/misc" element={
              <Overlay>
                <Misc><></></Misc>
              </Overlay>
            } />
            <Route path="/profile" element={
              <Overlay>
                <SidebarLayout title="Start">
                  <Profile />
                </SidebarLayout>
              </Overlay>
            } />
            <Route path="/profile/api" element={
              <Overlay>
                <SidebarLayout title="API keys" contentWidth="800px">
                  <ApiKeys />
                </SidebarLayout>
              </Overlay>
            } />
            <Route path="/over" element={
              <Overlay>
                <SidebarLayout title="Over het Dashboard Deelmobiliteit" contentWidth="900px">
                  <About />
                </SidebarLayout>
              </Overlay>
            } />
            <Route path="/functionaliteiten" element={
              <Overlay>
                <SidebarLayout title="Wat je met het Dashboard kunt" contentWidth="100%">
                  <Features />
                </SidebarLayout>
              </Overlay>
            } />
            <Route path="/veelgestelde-vragen" element={
              <Overlay>
                <SidebarLayout title="Veelgestelde vragen" contentWidth="100%">
                  <VeelgesteldeVragen />
                </SidebarLayout>
              </Overlay>
            } />
            <Route path="/export" element={
              <Overlay>
                <SidebarLayout title="Exporteer data">
                  <Export />
                </SidebarLayout>
              </Overlay>
            } />
            <Route path="/faq" element={
              <Overlay>
                <Misc>
                  <Faq />
                </Misc>
              </Overlay>
            } />
            <Route path="/faq/:path" element={
              <Overlay>
                <Misc>
                  <Faq />
                </Misc>
              </Overlay>
            } />
            <Route path="/docs" element={
              <Overlay>
                <SidebarLayout title="Documentatie" contentWidth="900px">
                  <Docs />
                </SidebarLayout>
              </Overlay>
            } />
            <Route path="/docs/:category" element={
              <Overlay>
                <SidebarLayout title="Documentatie" contentWidth="900px">
                  <Docs />
                </SidebarLayout>
              </Overlay>
            } />
            <Route path="/docs/:category/:doc" element={
              <Overlay>
                <SidebarLayout title="Documentatie" contentWidth="900px">
                  <Docs />
                </SidebarLayout>
              </Overlay>
            } />
            <Route path="/active_feeds" element={
              <Overlay>
                <SidebarLayout title="Actieve datafeeds">
                  <ActiveFeeds />
                </SidebarLayout>
              </Overlay>
            } />
          </>
          :
          null
        }

        { ! isLoggedIn ? <>
          <Route path="/" element={renderFilterbarElements()} />
          <Route path="/map/park" element={renderFilterbarElements()} />
          <Route path="/map/rentals" element={renderFilterbarElements()} />
          <Route path="/map/servicegebieden" element={renderFilterbarElements()} />
          <Route path="/map/beleidshubs" element={renderFilterbarElements()} />
          <Route path="/map/zones" element={renderFilterbarElements()} />
          <Route path="/misc" element={
            <Overlay>
              <Misc><></></Misc>
            </Overlay>
          } />
          <Route path="/profile" element={
            <Overlay>
              <SidebarLayout title="Start">
                <Profile />
              </SidebarLayout>
            </Overlay>
          } />
          <Route path="/faq" element={
            <Overlay>
              <Misc>
                <Faq />
              </Misc>
            </Overlay>
          } />
          <Route path="/faq/:path" element={
            <Overlay>
              <Misc>
                <Faq />
              </Misc>
            </Overlay>
          } />
        </> : '' }

        <Route path="/over" element={
          <Overlay>
            <SidebarLayout title="Over het Dashboard Deelmobiliteit" contentWidth="900px">
              <About />
            </SidebarLayout>
          </Overlay>
        } />
        <Route path="/functionaliteiten" element={
          <Overlay>
            <SidebarLayout title="Wat je met het Dashboard kunt" contentWidth="100%">
              <Features />
            </SidebarLayout>
          </Overlay>
        } />
        <Route path="/veelgestelde-vragen" element={
          <Overlay>
            <SidebarLayout title="Veelgestelde vragen" contentWidth="100%">
              <VeelgesteldeVragen />
            </SidebarLayout>
          </Overlay>
        } />
        <Route path="/stats/beleidsinfo" element={<>
          <Overlay>
            <Login />
          </Overlay>
          {renderFilterbarElements()}
        </>} />
        <Route path="/rondleiding" element={
          <ContentPage forceFullWidth={true}>
            <Tour />
          </ContentPage>
        } />
        <Route path="/login" element={
          <Overlay>
            <Login />
          </Overlay>
        } />
        <Route path="/reset-password/:changePasswordCode" element={
          <Overlay>
            <SetPassword />
          </Overlay>
        } />
        <Route path="/docs" element={
          <Overlay>
            <SidebarLayout title="Documentatie" contentWidth="900px">
              <Docs />
            </SidebarLayout>
          </Overlay>
        } />
        <Route path="/docs/:category" element={
          <Overlay>
            <SidebarLayout title="Documentatie" contentWidth="900px">
              <Docs />
            </SidebarLayout>
          </Overlay>
        } />
        <Route path="/docs/:category/:doc" element={
          <Overlay>
            <SidebarLayout title="Documentatie" contentWidth="900px">
              <Docs />
            </SidebarLayout>
          </Overlay>
        } />
        <Route path="/active_feeds" element={
          <Overlay>
            <SidebarLayout title="Actieve datafeeds">
              <ActiveFeeds />
            </SidebarLayout>
          </Overlay>
        } />
        <Route element={renderFilterbarElements()} />
      </Routes>

      <div key="mapContainer" ref={mapContainer} className="map-layer top-0"></div>
      {/*
       * Only mount the heavy background MapLibre map on routes that actually
       * show it. On routes like /stats/beleidsinfo the map sits behind an
       * opaque ContentPage overlay, so initializing MapLibre and downloading
       * its style/glyphs/sprites/tiles (including satellite tiles when the
       * user previously selected the satellite style) is pure waste. See
       * `src/helpers/routes.ts` for the allow-list.
       */}
      {pathRequiresBackgroundMap(pathName) && <MapPage mapContainer={mapContainer} />}

      <Menu pathName={pathName} />
      <IntroModal pathName={pathName} />

     </div>
     <Toaster />     
    </div>
  );
}

export default App;
